import svgIcons from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import SvgIcon from '@/components/atoms/SvgIcon';
import DoughnutChart, { TChartColorItem, TChartDataItem } from '@/components/molecules/DoughnutChart';
import ModalContainer from '@/components/molecules/ModalContainer';
import PeerSupportContactReportSkeleton from '@/components/molecules/Skeletons/PeerSupportContactReportSkeleton';
import {
  PEER_SUPPORT_EVENT_OUTCOME_DISPLAY_LABELS,
  WELLNESS_DOMAINS_DISPLAY_LABELS,
  WELLNESS_DOMAINS_DISPLAY_LABELS_REVERSE,
  WELLNESS_SUB_DOMAINS_DISPLAY_LABELS,
} from '@/constants';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { CHART_COLORS_ARRAY, PEER_SUPPORT_OUTCOME_COLORS_ARRAY } from '@/constants/ui';
import usePeerSupportEngagement from '@/hooks/peersupport/usePeerSupportEngagement';
import PeerSupportDashboardHistoricalChart from '@/modules/peer-support/PeerSupportDashboardHistoricalChart';
import PeerSupportNewContactModal from '@/modules/peer-support/PeerSupportNewContactModal';
import colors from '@/theme/colors';
import { THealthDomain, THealthSubDomain } from '@/types/Health.types';
import { TPeerSupportEventOutcome } from '@/types/PeerSupport.types';
import { HStack, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

const SUBJECTS_CHART_DOMAINS_LIST: THealthDomain[] = [
  'physical',
  'mental',
  'social',
  'vocational',
  'spiritual',
  'purpose',
  'fun',
];

const SUBJECTS_CHART_COLORS = CHART_COLORS_ARRAY.slice(0, 7);

interface Props {
  canLogContact?: boolean;
  // When true, drops the page-level chrome (title + "View Peer Support Team" button,
  // outer card shadow / width / padding) so the section can be embedded inside another
  // container (e.g. the Wholeness Report modal).
  embedded?: boolean;
  // Optional controlled state for the selected month (`yyyy-m`, 1-indexed). When provided
  // the component is fully controlled; otherwise it falls back to its own internal state.
  month?: string;
  onMonthChange?: (month: string) => void;
}

const PeerSupportDashboardOverview = ({
  canLogContact = true,
  embedded = false,
  month: controlledMonth,
  onMonthChange,
}: Props) => {
  const navigate = useNavigate();

  const currentMonth = new Date().getMonth() + 1;
  const initialDate = `${new Date().getFullYear()}-${currentMonth}`;

  const [internalMonth, setInternalMonth] = useState(initialDate);
  const dashboardMonthData = controlledMonth ?? internalMonth;
  const setDashboardMonthData = onMonthChange ?? setInternalMonth;
  const [dashboardMonthDataToDisplay, setDashboardMonthDataToDisplay] = useState('');

  // just adds a `0` to the month part of the yyyy-mm date, if the month is less than 10
  useEffect(() => {
    const splitDashboardMonthDate = dashboardMonthData.split('-');
    const displayYear = splitDashboardMonthDate[0];

    const displayMonthNumber = parseInt(splitDashboardMonthDate[1]); // 1 - 12
    let displayMonthString = displayMonthNumber.toString();

    if (displayMonthNumber < 10) {
      displayMonthString = `0${displayMonthString}`;
    }

    const newDashboardMonthDataToDisplay = `${displayYear}-${displayMonthString}`;
    setDashboardMonthDataToDisplay(newDashboardMonthDataToDisplay);
  }, [dashboardMonthData]);

  const { data: peerSupportEngagement, isLoading, isRefetching } = usePeerSupportEngagement(dashboardMonthData);

  const sixMonthTotal = useMemo(
    () => peerSupportEngagement?.historicalData.reduce((s, m) => s + m.totalInteractions, 0) ?? 0,
    [peerSupportEngagement?.historicalData],
  );

  const selectedMonthEntry = useMemo(
    () => peerSupportEngagement?.historicalData.find((entry) => entry.month === dashboardMonthData),
    [peerSupportEngagement?.historicalData, dashboardMonthData],
  );

  const selectedMonthContacts = selectedMonthEntry?.totalInteractions ?? 0;
  const selectedMonthPeerReported = selectedMonthEntry?.peerReported ?? 0;

  const activeMonthTooltip = useMemo(() => {
    const subjects = peerSupportEngagement?.aggregates.subjects ?? {};
    const mentions = Object.values(subjects).reduce((s, d) => s + d.count, 0);
    const domainsTouched = Object.keys(subjects).length;
    const monthLabel = format(
      dashboardMonthDataToDisplay ? new Date(`${dashboardMonthDataToDisplay.replace(/-/g, '/')}/01`) : new Date(),
      "MMMM ''yy",
    );

    if (selectedMonthPeerReported === 0) return `No peer-reported contacts logged in ${monthLabel}`;

    const contactStr = `${selectedMonthPeerReported} ${selectedMonthPeerReported === 1 ? 'contact' : 'contacts'}`;
    const topicStr = `${mentions} subdomain ${mentions === 1 ? 'topic' : 'topics'}`;
    const domainStr = `${domainsTouched} ${domainsTouched === 1 ? 'domain' : 'domains'}`;

    return `${monthLabel}: ${contactStr} mentioned ${topicStr} across ${domainStr}`;
  }, [peerSupportEngagement?.aggregates.subjects, selectedMonthPeerReported, dashboardMonthDataToDisplay]);

  const [contactModalOpen, setContactModalOpen] = useState(false);

  const [subjectsPerDomainChartList, subdomainsPerDomainChartData] = useMemo(() => {
    const subjectsList = Object.entries(peerSupportEngagement?.aggregates.subjects ?? {});

    const subjectsSortedPerDesign = [
      subjectsList.find((subj) => subj[0] === 'physical'),
      subjectsList.find((subj) => subj[0] === 'mental'),
      subjectsList.find((subj) => subj[0] === 'social'),
      subjectsList.find((subj) => subj[0] === 'vocational'),
      subjectsList.find((subj) => subj[0] === 'spiritual'),
      subjectsList.find((subj) => subj[0] === 'purpose'),
      subjectsList.find((subj) => subj[0] === 'fun'),
    ].filter((elem) => elem !== undefined);

    const domainsList: TChartDataItem[] = [];
    const subdomainsList: any = {};

    subjectsSortedPerDesign.forEach(([key, value]) => {
      domainsList.push({
        name: WELLNESS_DOMAINS_DISPLAY_LABELS[key as THealthDomain],
        value: value.count,
        description: 'Click to view Subdomains',
      });

      const keyDomainSubdomainsList: TChartDataItem[] = [];
      Object.entries(value.subdomains).forEach(([key, value]) => {
        keyDomainSubdomainsList.push({
          name: WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[key as THealthSubDomain],
          value: value,
          description: `${WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[key as THealthSubDomain]}: ${value}`,
        });
      });
      subdomainsList[key] = keyDomainSubdomainsList;
    });

    return [domainsList, subdomainsList];
  }, [peerSupportEngagement?.aggregates.subjects]);

  const subjectsPerDomainChartColors: TChartColorItem[] = useMemo(
    () =>
      SUBJECTS_CHART_COLORS.map((color, i) => ({
        name: subjectsPerDomainChartList?.[i]
          ? subjectsPerDomainChartList?.[i].name
          : WELLNESS_DOMAINS_DISPLAY_LABELS[SUBJECTS_CHART_DOMAINS_LIST[i] as THealthDomain],
        value: color,
      })),
    [subjectsPerDomainChartList],
  );

  // Section selection and subdomains modal and chart
  const [selectedChartSectionLabel, setSelectedChartSectionLabel] = useState('');
  const [subDomainModalOpen, setSubDomainModalOpen] = useState(false);

  const updateSelectedChartSection = (chartSectionLabel: string) => {
    setSelectedChartSectionLabel(chartSectionLabel);
    setSubDomainModalOpen(true);
  };

  const subdomainsChartData = useMemo(
    () => subdomainsPerDomainChartData[WELLNESS_DOMAINS_DISPLAY_LABELS_REVERSE[selectedChartSectionLabel]],
    [subdomainsPerDomainChartData, selectedChartSectionLabel],
  );

  const subdomainsChartColors: TChartColorItem[] = useMemo(() => {
    if (selectedChartSectionLabel) {
      const subdomainsToDisplay =
        subdomainsPerDomainChartData[WELLNESS_DOMAINS_DISPLAY_LABELS_REVERSE[selectedChartSectionLabel]];

      const subdomainChartColors = CHART_COLORS_ARRAY.slice(0, subdomainsToDisplay?.length);

      return subdomainChartColors.map((color, i) => {
        const subdomain = subdomainsToDisplay?.[i]?.name;

        return {
          name: subdomain,
          value: color,
        };
      });
    }

    return [];
  }, [subdomainsPerDomainChartData, selectedChartSectionLabel]);

  // Outcomes chart
  const outcomesChartData = useMemo(() => {
    const outcomes = peerSupportEngagement?.aggregates.outcomes ?? {};

    const outcomesList: TChartDataItem[] = [];

    Object.entries(outcomes).forEach(([key, value]) => {
      outcomesList.push({
        name: PEER_SUPPORT_EVENT_OUTCOME_DISPLAY_LABELS[key as TPeerSupportEventOutcome],
        value: value as number,
      });
    });

    return outcomesList.sort((a, b) => (a.value > b.value ? -1 : 1));
  }, [peerSupportEngagement?.aggregates.outcomes]);

  const isSubjectsChartDataEmpty = subjectsPerDomainChartList.length === 0;
  const isOutcomesChartDataEmpty = outcomesChartData.length === 0;

  const isDoughnutChartsDataEmpty = isSubjectsChartDataEmpty && isOutcomesChartDataEmpty;

  // No data anywhere in the 6-month window — render a single full-width empty state instead
  // of the 3-column layout (which otherwise leaves the empty message squeezed in 2/3 width).
  const isContactsHistoryEmpty = (peerSupportEngagement?.historicalData?.length ?? 0) === 0;
  const isCompletelyEmpty = isContactsHistoryEmpty && isDoughnutChartsDataEmpty;

  const outcomesChartColors = useMemo(() => {
    const chartColors = outcomesChartData.map((item, i) => ({
      name: item.name,
      value: PEER_SUPPORT_OUTCOME_COLORS_ARRAY[i],
    }));

    return chartColors;
  }, [outcomesChartData]);

  return (
    <VStack width={embedded ? '100%' : MAX_CONTAINER_WIDTH}>
      {!embedded && (
        <HStack width={'100%'} justifyContent={'space-between'} alignItems={'center'} marginBottom={'55px'}>
          <Text variant={'loraMediumTitle'}>Peer Support Contact Report</Text>
          <CustomButton
            noShadow
            hoverOpacityDrop
            backgroundColor={colors.extra.white}
            labelColor={colors.text.darkBlue}
            borderColor={colors.text.mediumBlue}
            label="View Peer Support Team"
            iconAfterLabelSvgPath={svgIcons.rightArrow}
            iconAfterLabelSvgSize={12}
            iconAfterLabelSvgBoxHeight={14}
            iconAfterLabelSvgBoxWidth={14}
            height={'54px'}
            width={'240px'}
            style={{ borderWidth: '2px' }}
            onClick={() => navigate({ to: '/directory/peer-support' })}
          />
        </HStack>
      )}

      {/* Actual page content */}
      <VStack
        width={'100%'}
        borderRadius={embedded ? '0' : '20px'}
        backgroundColor={embedded ? 'transparent' : 'extra.white'}
        padding={embedded ? '0' : '38px'}
        position={'relative'}
        boxShadow={embedded ? 'none' : '0px 10px 80px 0px #00417926'}
      >
        <HStack justifyContent={canLogContact ? 'space-between' : 'center'} width={'100%'} marginBottom={'40px'}>
          <VStack alignItems={canLogContact ? 'flex-start' : 'center'} gap={'4px'}>
            <Text variant="loraMedium22" textAlign={canLogContact ? 'start' : 'center'}>
              Engagement Data
            </Text>

            <Text
              variant="urbanistExtraBold"
              textAlign={canLogContact ? 'start' : 'center'}
              color={'text.mediumGray'}
              width={'100%'}
            >
              {format(
                !!dashboardMonthDataToDisplay
                  ? new Date(`${dashboardMonthDataToDisplay.replace(/-/g, '/')}/01`)
                  : new Date(),
                "MMMM ''yy",
              )}{' '}
              |{' '}
              <Text as="span" color={'text.darkBlue'} fontWeight={700}>
                {selectedMonthContacts} {selectedMonthContacts === 1 ? 'Contact' : 'Contacts'}
              </Text>
            </Text>
          </VStack>

          <HStack gap={'20px'}>
            {isRefetching && !embedded && (
              <LoadingLogo width="42px" spinnerSize="42px" spacing="10px" showsText={false} />
            )}
            {canLogContact && (
              <CustomButton
                backgroundColor="primary.500"
                labelColor="extra.white"
                label="Log Contact"
                disabled={isRefetching || isLoading}
                iconBeforeLabelJSX={
                  <HStack width={'100%'} position={'absolute'} right={'20px'} justifyContent={'flex-end'}>
                    <SvgIcon iconPath={svgIcons.rightArrow} color={colors.extra.white} size={'14'} />
                  </HStack>
                }
                labelStyle={{ marginRight: '22px' }}
                width={'170px'}
                onClick={() => setContactModalOpen(true)}
              />
            )}
          </HStack>
        </HStack>

        {isLoading && <PeerSupportContactReportSkeleton />}

        {!isLoading && isCompletelyEmpty && (
          <VStack width={'100%'} gap={'6px'} paddingY={'20px'}>
            <Text
              variant={'loraSmallTitle'}
              fontStyle={'italic'}
              fontWeight={500}
              textAlign={'center'}
              whiteSpace={'pre-wrap'}
            >
              No peer support contact data available for{' \n'}
              {format(
                !!dashboardMonthDataToDisplay
                  ? new Date(`${dashboardMonthDataToDisplay.replace(/-/g, '/')}/01`)
                  : new Date(),
                'MMMM yyyy',
              )}
              .
            </Text>
            <Text variant={'urbanistRegular'} fontSize={'14px'} color={'text.mediumGray'} textAlign={'center'}>
              Select a different month to view data.
            </Text>
          </VStack>
        )}

        {!isLoading && !isCompletelyEmpty && (
          <HStack
            width={'100%'}
            height={'100%'}
            justifyContent={'space-between'}
            alignItems={'flex-start'}
            gap={'40px'}
          >
            <VStack justifyContent={'start'} alignItems={'start'} height={'100%'} flex={1}>
              <ChartSectionTitle title={'Contacts'} additionalText={`6 Month Total: ${sixMonthTotal}`} />
              <VStack>
                <PeerSupportDashboardHistoricalChart
                  data={peerSupportEngagement?.historicalData ?? []}
                  onBarClick={(month) => setDashboardMonthData(month)}
                  selectedMonth={dashboardMonthData}
                  activeMonthTooltip={activeMonthTooltip}
                />
              </VStack>
            </VStack>

            <HStack flex={2} justifyContent={'start'} alignItems={'start'} height={'100%'}>
              {!isDoughnutChartsDataEmpty && (
                <HStack flex={1} justifyContent={'space-between'} gap={'40px'}>
                  <VStack flex={1} height={'100%'} justifyContent={'center'} alignItems={'center'}>
                    <ChartSectionTitle title="Subjects" />
                    <VStack align={'start'} width={'100%'}>
                      <DoughnutChart
                        data={subjectsPerDomainChartList}
                        colors={subjectsPerDomainChartColors}
                        chartSvgWidth={180}
                        onChartSectionClick={(section: string) => updateSelectedChartSection(section)}
                        hasShadow={false}
                        chartPadding={0}
                        chartShadowPadding={10}
                        chartAndLegendGap="20px"
                        legendItemsVerticalGap="8px"
                        legendDotAndTextSize="12px"
                        hoverCorrection={-50}
                        hoverTooltipSide="left"
                        tooltipElementConfig={{ hasDescription: true, width: '190px' }}
                        legendLabelIncludesValue
                      />
                    </VStack>
                  </VStack>

                  <VStack flex={1} height={'100%'} justifyContent={'flex-end'} alignItems={'center'}>
                    <ChartSectionTitle title="Outcomes" />
                    <VStack align={'start'} width={'100%'}>
                      <DoughnutChart
                        additionalSvgId="outcomes"
                        data={outcomesChartData}
                        colors={outcomesChartColors}
                        chartSvgWidth={180}
                        hasShadow={false}
                        chartPadding={0}
                        chartShadowPadding={10}
                        chartAndLegendGap="20px"
                        legendItemsVerticalGap="8px"
                        legendDotAndTextSize="12px"
                        hoverCorrection={-50}
                        tooltipElementConfig={{}}
                        legendLabelIncludesValue
                      />
                    </VStack>
                  </VStack>
                </HStack>
              )}
              {isDoughnutChartsDataEmpty && (
                <VStack flex={1}>
                  <Text variant={'loraSmallTitle'} fontStyle={'italic'} fontWeight={500} whiteSpace={'pre-wrap'}>
                    No peer support contact data available for{' \n'}
                    {format(
                      !!dashboardMonthDataToDisplay
                        ? new Date(`${dashboardMonthDataToDisplay.replace(/-/g, '/')}/01`)
                        : new Date(),
                      'MMMM yyyy',
                    )}
                    .
                  </Text>
                  <Text variant={'urbanistRegular'} fontSize={'14px'} color={'text.mediumGray'} textAlign={'center'}>
                    Select a different month to view data.
                  </Text>
                </VStack>
              )}
            </HStack>
          </HStack>
        )}

        {/* Removed from design */}
        {/* {!isLoading && <PeerSupportTimeline timelineEvents={peerSupportDashboard?.events ?? []} />} */}
      </VStack>

      {canLogContact && <PeerSupportNewContactModal isOpen={contactModalOpen} setIsOpen={setContactModalOpen} />}

      {/* Subdomain modal */}
      <ModalContainer
        isOpen={subDomainModalOpen}
        onClose={() => setSubDomainModalOpen(false)}
        isBlur={false}
        width="55vw"
        height="40vh"
        backgroundColor={colors.background.lightBlue}
      >
        <VStack padding={'20px'} gap={'10px'}>
          <Text variant={'loraSmallTitle'} marginBottom={'30px'} fontWeight={500}>
            {selectedChartSectionLabel} Wholeness Subdomains
          </Text>
          <DoughnutChart
            additionalSvgId="subdomains"
            data={subdomainsChartData}
            colors={subdomainsChartColors}
            chartSvgWidth={300}
            legendItemsVerticalGap="12px"
            legendDotAndTextSize="18px"
            tooltipElementConfig={{}} // makes the tooltip not display, while keeping the hover behavior
            legendLabelIncludesValue
          />
        </VStack>
      </ModalContainer>
    </VStack>
  );
};

const ChartSectionTitle = ({ title, additionalText }: { title: string; additionalText?: string }) => {
  return (
    <HStack width={'100%'} height={'100%'} justifyContent={'space-between'} alignItems={'flex-start'} gap={'5px'}>
      <Text variant={'urbanistBold'} color={'text.darkBlue'}>
        {title}
      </Text>
      <Text variant={'urbanistBold'} color={'text.mediumGray'}>
        {additionalText}
      </Text>
    </HStack>
  );
};

export default PeerSupportDashboardOverview;
